// Hiragana101.jsx
import React, { useMemo } from 'react';
import { Box, Paper, Typography, Button, IconButton } from '@mui/material';
import { Play } from 'lucide-react';
import { motion } from 'framer-motion';
import { useThemeContext } from './ThemeWrapper';
import Sidebar from './Sidebar';
import RightPanel from './RightPanel';

const hiragana101Data = [
  { title: 'Hiragana す, し, う, こ', data: [['す', 'su'], ['し', 'shi'], ['う', 'u'], ['こ', 'ko']] },
  { title: 'あ, か, さ, い', data: [['あ', 'a'], ['か', 'ka'], ['さ', 'sa'], ['い', 'i']] },
  { title: 'え, み, ち, わ', data: [['え', 'e'], ['み', 'mi'], ['ち', 'chi'], ['わ', 'wa']] },
  { title: 'き, ん, お, た', data: [['き', 'ki'], ['ん', 'n'], ['お', 'o'], ['た', 'ta']] },
  { title: 'り, ら, つ, ゆ', data: [['り', 'ri'], ['ら', 'ra'], ['つ', 'tsu'], ['ゆ', 'yu']] },
  { title: 'は, そ, ま, て', data: [['は', 'ha'], ['そ', 'so'], ['ま', 'ma'], ['て', 'te']] },
  { title: 'く, も, せ, な', data: [['く', 'ku'], ['も', 'mo'], ['せ', 'se'], ['な', 'na']] },
  { title: 'ふ, け, ね, に', data: [['ふ', 'fu'], ['け', 'ke'], ['ね', 'ne'], ['に', 'ni']] },
  { title: 'ぬ, ひ, ほ, る', data: [['ぬ', 'nu'], ['ひ', 'hi'], ['ほ', 'ho'], ['る', 'ru']] },
  { title: 'へ, や, の, む, れ', data: [['へ', 'he'], ['や', 'ya'], ['の', 'no'], ['む', 'mu'], ['れ', 're']] },
  { title: 'を, よ, め, と, ろ', data: [['を', 'o'], ['よ', 'yo'], ['め', 'me'], ['と', 'to'], ['ろ', 'ro']] },
  {
    title: 'Dakuten',
    data: [
      ['が', 'ga'], ['ぎ', 'gi'], ['ぐ', 'gu'], ['げ', 'ge'], ['ご', 'go'],
      ['ざ', 'za'], ['ず', 'zu'], ['ぜ', 'ze'], ['ぞ', 'zo'], ['じ', 'ji'],
      ['だ', 'da'], ['で', 'de'], ['ど', 'do'], ['ぢ', 'ji'], ['づ', 'zu'],
      ['ば', 'ba'], ['び', 'bi'], ['ぶ', 'bu'], ['べ', 'be'], ['ぼ', 'bo'],
      ['ぱ', 'pa'], ['ぴ', 'pi'], ['ぷ', 'pu'], ['ぺ', 'pe'], ['ぽ', 'po'],
    ]
  },
  {
    title: 'Small Kana',
    data: [
      ['ひゃ', 'hya'], ['みゃ', 'mya'], ['りゃ', 'rya'], ['きゃ', 'kya'], ['にゃ', 'nya'], ['しゃ', 'sha'], ['ちゃ', 'cha'],
      ['りゅ', 'ryu'], ['きゅ', 'kyu'], ['しゅ', 'shu'], ['ちゅ', 'chu'], ['にゅ', 'nyu'], ['ひゅ', 'hyu'], ['みゅ', 'myu'],
      ['きょ', 'kyo'], ['にょ', 'nyo'], ['ひょ', 'hyo'], ['みょ', 'myo'], ['りょ', 'ryo'], ['しょ', 'sho'], ['ちょ', 'cho'],
      ['ぎゃ', 'gya'], ['じゃ', 'ja'], ['ぢゃ', 'ja'], ['びゃ', 'bya'], ['ぴゃ', 'pya'],
      ['ぎゅ', 'gyu'], ['じゅ', 'ju'], ['びゅ', 'byu'], ['ぴゅ', 'pyu'], ['りゅ', 'ryu'],
      ['ぎょ', 'gyo'], ['じょ', 'jo'], ['ぢょ', 'jo'], ['びょ', 'byo'], ['ぴょ', 'pyo'],
    ]
  },
  {
    title: 'Spelling',
    data: [
      ['っ+k', 'kk'], ['っ+p', 'pp'], ['っ+s', 'ss'], ['っ+t', 'tt'],
      ['っ+cch', 'cch'], ['っ+ssh', 'ssh'], ['っ+ttsu', 'ttsu']
    ]
  },
  {
    title: 'Long Vowels',
    data: [['ああ', 'aa'], ['いい', 'ii'], ['うう', 'uu'], ['ええ', 'ee'], ['おお', 'oo'], ['えい', 'ei'], ['おう', 'ou']]
  }
];

// simple motion variants
const containerV = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const itemV = { hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 120, damping: 14 } } };

export default function Hiragana101() {
  const { isDarkTheme, toggleTheme, bgPrimary, bgSecondary, textPrimary, textSecondary, borderColor } = useThemeContext();

  // flatten for main grid (exclude specialist sections)
  const mainChars = useMemo(
    () =>
      hiragana101Data
        .filter(s => !['Dakuten', 'Small Kana', 'Spelling', 'Long Vowels'].includes(s.title))
        .flatMap(s => s.data.map(([k, r]) => ({ kana: k, romaji: r }))),
    []
  );

  const playAudio = (romaji) => {
    if (!romaji) return;
    const clean = String(romaji).split('/')[0].toLowerCase().trim();
    const url = `${process.env.PUBLIC_URL}/audio/${clean}.mp3`;
    const audio = new Audio(url);
    audio.play().catch(err => console.warn('Audio not found:', url, err));
  };

  // translucent button styles using template literals
  const translucentButtonClass = isDarkTheme
    ? 'backdrop-blur-md !bg-gray-800/40 hover:!bg-gray-800/60 !text-white'
    : 'backdrop-blur-md !bg-gray-300/40 hover:!bg-gray-300/60 !text-black';

  // inline styles from ThemeWrapper (js values) — fall back if not provided
  const pageStyle = {
    backgroundColor: bgPrimary ?? (isDarkTheme ? '#121212' : '#ffffff'),
    color: textPrimary ?? (isDarkTheme ? '#fff' : '#000'),
    minHeight: '100vh'
  };

  const cardStyle = {
    backgroundColor: bgSecondary ?? (isDarkTheme ? '#1e1e1e' : '#f5f5f5'),
    border: `1px solid ${borderColor ?? (isDarkTheme ? '#333' : '#ddd')}`,
    color: textPrimary ?? (isDarkTheme ? '#fff' : '#000')
  };

  return (
    <div style={pageStyle} className="flex">
      <Sidebar />

      <main style={{ flex: 1, padding: 24 }}>
        

        {/* MAIN GRID */}
        <motion.div initial="hidden" animate="visible" variants={containerV}>
          <Typography variant="h6" sx={{ mb: 1, color: textSecondary }}>Main Kana</Typography>

          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))', gap: 12 }}>
              {mainChars.map((c, i) => (
                <motion.div key={i} variants={itemV}>
                  <Paper
                    elevation={3}
                    onClick={() => playAudio(c.romaji)}
                    role="button"
                    aria-label={`Play ${c.romaji}`}
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      borderRadius: 2,
                      transition: 'transform 160ms ease, box-shadow 160ms ease',
                      '&:hover': { transform: 'translateY(-6px)' },
                    }}
                    style={cardStyle}
                  >
                    <div style={{ fontSize: 36, fontWeight: 800 }}>{c.kana}</div>
                    <div style={{ color: textSecondary, marginTop: 6 }}>{c.romaji}</div>
                    <IconButton
                      onClick={(e) => { e.stopPropagation(); playAudio(c.romaji); }}
                      aria-label={`Play ${c.romaji}`}
                      size="small"
                      sx={{ mt: 1 }}
                      style={{ color: textSecondary }}
                    >
                      <Play size={16} />
                    </IconButton>
                  </Paper>
                </motion.div>
              ))}
            </div>
          </div>

          {/* FULL TIP SECTIONS */}
          <div>
            {hiragana101Data.map((section, sidx) => (
              <div key={sidx} style={{ marginBottom: 20 }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>{section.title}</Typography>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {section.data.map(([kana, romaji], idx) => (
                    <motion.div key={idx} variants={itemV}>
                      <Button
                        onClick={() => playAudio(romaji)}
                        className={`!px-3 !py-2 !rounded-md ${isDarkTheme ? '!bg-gray-800 hover:!bg-gray-700 !text-white' : '!bg-gray-100 hover:!bg-gray-200 !text-black'}`}
                        sx={{ textTransform: 'none', fontFamily: 'monospace' }}
                      >
                        <span style={{ fontWeight: 700, fontSize: 18, marginRight: 8 }}>{kana}</span>
                        <span style={{ color: textSecondary, fontSize: 13 }}>{romaji}</span>
                      </Button>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </main>

      <div style={{ width: 320, borderLeft: `1px solid ${borderColor ?? '#ddd'}` }} className="hidden lg:block">
        <RightPanel />
      </div>
    </div>
  );
}
